import type { FormEvent, ReactNode } from 'react'
import {
  AleloButton,
  AleloError,
  AleloInput,
  AleloPhoneInput,
} from '@/components/AleloLayout'
import styles from '@/components/AleloLayout.module.css'
import locales from './locales/pt-BR.json'

type Field = {
  id: string
  name: string
  /** `tel` desenha o prefixo fixo `+55`: a tela só aceita celular do Brasil. */
  type: 'email' | 'tel'
  autoComplete: string
  inputMode?: 'email' | 'tel'
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  maxLength?: number
}

type Props = {
  title: string
  description: string
  field: Field
  /** Presente depois que o código foi enviado: o campo trava e o código entra. */
  code?: { value: string; onChange: (value: string) => void }
  /** Volta o passo ao início (o BFF prende a identidade da sessão ao primeiro envio). */
  onFix?: () => void
  resend?: { seconds: number; onResend: () => void }
  /** Marca o canal já confirmado, acima do botão de avançar. */
  confirmedHint?: string
  submitLabel: string
  /** Erro de formato do contato, mostrado no próprio campo. */
  fieldError?: string
  error?: string
  pending: boolean
  onSubmit: (event: FormEvent) => void
  onBack: () => void
  children?: ReactNode
}

/**
 * Passo do cadastro que coleta um contato e, quando o canal é exigido, confirma
 * a posse dele pelo código: "Verificar" dispara o envio, o campo de código
 * aparece, e o passo só avança com o canal confirmado. Toda a decisão de fluxo
 * fica em quem renderiza — aqui é só apresentação.
 */
export function VerifyStep({
  title,
  description,
  field,
  code,
  onFix,
  resend,
  confirmedHint,
  submitLabel,
  fieldError,
  error,
  pending,
  onSubmit,
  onBack,
  children,
}: Props) {
  const locked = Boolean(code) || Boolean(confirmedHint)

  return (
    <>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.subtitle}>{description}</p>
      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        {field.type === 'tel' ? (
          <AleloPhoneInput
            id={field.id}
            name={field.name}
            autoComplete={field.autoComplete}
            label={field.label}
            placeholder={field.placeholder}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            error={fieldError}
            readOnly={locked}
          />
        ) : (
          <AleloInput
            id={field.id}
            name={field.name}
            type={field.type}
            inputMode={field.inputMode}
            autoComplete={field.autoComplete}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={field.maxLength}
            label={field.label}
            placeholder={field.placeholder}
            value={field.value}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            error={fieldError}
            readOnly={locked}
          />
        )}
        {locked && onFix ? (
          <div className={styles.forgotRow}>
            <AleloButton variant="link" onClick={onFix} disabled={pending}>
              {locales.fix}
            </AleloButton>
          </div>
        ) : null}

        {code ? (
          <AleloInput
            id={`${field.id}_otp`}
            name="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            label={locales.otpLabel}
            placeholder={locales.otpPlaceholder}
            value={code.value}
            onChange={(e) =>
              code.onChange(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            maxLength={6}
          />
        ) : null}

        {confirmedHint ? (
          <p className={styles.confirmedHint}>{confirmedHint}</p>
        ) : null}

        {children}
        <AleloError message={error} />
        <AleloButton type="submit" disabled={pending}>
          {submitLabel}
        </AleloButton>

        <div className={styles.linkRow}>
          {resend ? (
            <AleloButton
              variant="link"
              disabled={pending || resend.seconds > 0}
              onClick={resend.onResend}
            >
              {resend.seconds > 0
                ? `${locales.resend} (${resend.seconds}s)`
                : locales.resend}
            </AleloButton>
          ) : null}
          <AleloButton variant="link" onClick={onBack}>
            {locales.back}
          </AleloButton>
        </div>
      </form>
    </>
  )
}
